import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User, ChevronDown, Phone, Mail, MapPin, Search } from "lucide-react";
import Logo from "./Logo";
import { categories, company } from "../mock";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setProductsOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: "Cataloghi", to: "/cataloghi" },
    { label: "News & Novità", to: "/news" },
    { label: "Servizi", to: "/servizi" },
    { label: "Chi siamo", to: "/chi-siamo" },
    { label: "Contatti", to: "/contatti" },
  ];

  return (
    <header className="w-full sticky top-0 z-50">
      {/* Top utility bar */}
      <div className="bg-[#0B2545] text-white text-xs hidden md:block">
        <div className="max-w-[1400px] mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[#00A859]" />
              {company.address}, {company.city}
            </span>
            <a href={`tel:${company.telRaw}`} className="flex items-center gap-1.5 hover:text-[#00A859] transition-colors">
              <Phone className="w-3.5 h-3.5 text-[#00A859]" /> {company.tel}
            </a>
            <a href={`mailto:${company.email}`} className="flex items-center gap-1.5 hover:text-[#00A859] transition-colors">
              <Mail className="w-3.5 h-3.5 text-[#00A859]" /> {company.email}
            </a>
          </div>
          <div className="flex items-center gap-4 text-gray-300">
            <span>Dedicato ai professionisti</span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className={`bg-white border-b transition-shadow ${scrolled ? "shadow-md" : ""}`}>
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between gap-6">
          <Link to="/" className="shrink-0">
            <Logo />
          </Link>

          <div className="hidden lg:flex flex-1 max-w-xl mx-6">
            <div className="flex items-center w-full border border-gray-200 rounded-full overflow-hidden focus-within:border-[#00A859] transition-colors">
              <input
                type="text"
                placeholder="Cerca prodotti, categorie, codici..."
                className="flex-1 px-5 py-2.5 text-sm outline-none"
              />
              <button className="bg-[#00A859] hover:bg-[#00874A] text-white px-5 py-2.5 transition-colors">
                <Search className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button className="hidden md:flex items-center gap-2 border border-gray-200 rounded-full px-4 py-2 text-sm font-semibold text-[#0B2545] hover:border-[#00A859] hover:text-[#00A859] transition-colors">
              <User className="w-4 h-4" />
              <span>Accedi / Registrati</span>
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <button
              className="lg:hidden p-2 text-[#0B2545]"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Secondary nav */}
        <nav className="border-t border-gray-100 bg-white hidden lg:block">
          <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => setProductsOpen(!productsOpen)}
                className="flex items-center gap-2 bg-[#00A859] hover:bg-[#00874A] text-white px-5 py-3.5 font-bold text-sm uppercase tracking-wide transition-colors"
              >
                <Menu className="w-4 h-4" /> Prodotti
              </button>
              {navLinks.slice(0, 3).map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="px-5 py-3.5 text-sm font-semibold uppercase tracking-wide text-[#0B2545] hover:text-[#00A859] transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </div>
            <div className="flex items-center">
              {navLinks.slice(3).map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="px-5 py-3.5 text-sm font-semibold text-[#0B2545] hover:text-[#00A859] transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Mega menu */}
          {productsOpen && (
            <div className="absolute left-0 right-0 bg-white shadow-2xl border-t border-gray-100 z-40">
              <div className="max-w-[1400px] mx-auto px-6 py-8 grid grid-cols-3 gap-8">
                {categories.map((cat) => (
                  <div key={cat.slug}>
                    <Link
                      to={`/prodotti/${cat.slug}`}
                      className="font-bold text-[#0B2545] hover:text-[#00A859] uppercase tracking-wide text-sm mb-3 inline-block transition-colors"
                    >
                      {cat.name}
                    </Link>
                    <ul className="space-y-1.5">
                      {cat.sub.slice(0, 5).map((s) => (
                        <li key={s}>
                          <Link
                            to={`/prodotti/${cat.slug}`}
                            className="text-sm text-gray-600 hover:text-[#00A859] transition-colors"
                          >
                            {s}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </nav>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-gray-100">
          <div className="px-6 py-4 space-y-2">
            <Link to="/prodotti" className="block py-2 font-semibold text-[#0B2545]">Prodotti</Link>
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} className="block py-2 text-[#0B2545]">
                {l.label}
              </Link>
            ))}
            <a href={`tel:${company.telRaw}`} className="block py-2 text-[#00A859] font-semibold">
              {company.tel}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
